import {HttpClient} from '@angular/common/http';
import {computed, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ListState} from '@domain/list-response';
import {Notification} from '@domain/notification/notification';
import {RequestStatus} from '@infrastructure/request-status.enum';
import {TranslateService} from '@ngx-translate/core';
import {SnackBarService} from '@shared/snackbar/snackbar.service';
import {catchError, EMPTY, map, retry, Subject, switchMap} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class NotificationService
{
    private readonly httpClient = inject(HttpClient);

    public readonly snackBar = inject(SnackBarService);

    public readonly translate = inject(TranslateService);

    private readonly state = signal<ListState<Notification>>({
        status: RequestStatus.NOT_EXECUTED,
        items: [],
        total: 0,
        details: undefined
    });

    public readonly notifications = computed(() => this.state().items);

    public readonly total = computed(() => this.state().total);

    private readonly get$ = new Subject<void>();

    private readonly notificationsFetched$ = this.get$.pipe(
        switchMap(() =>
        {
            return this.httpClient.get<Notification[]>('notification').pipe(
                catchError(() =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR
                    });

                    return EMPTY;
                }),
                map((notifications: Notification[]) => ({
                    total: notifications.length,
                    items: notifications.map(Notification.fromJSON)
                })),
                retry({delay: 600000})
            );
        })
    );

    private readonly markAsRead$ = new Subject<string>();

    private readonly notificationMarkedAsRead$ = this.markAsRead$.pipe(
        switchMap((id: string) =>
        {
            return this.httpClient.patch(`notification/${id}`, null).pipe(
                catchError(() =>
                {
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.NOTIFICATION_NOT_READ'));

                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR
                    });

                    return EMPTY;
                })
            );
        })
    );

    public constructor()
    {
        this.notificationsFetched$.pipe(takeUntilDestroyed()).subscribe({
            next: ({total, items}: {total: number; items: Notification[]}) =>
            {
                this.state.set({
                    ...this.state(),
                    status: RequestStatus.CONCLUDED,
                    items,
                    total
                });
            }
        });

        this.notificationMarkedAsRead$.pipe(takeUntilDestroyed()).subscribe({
            next: () =>
            {
                this.state.set({
                    ...this.state(),
                    status: RequestStatus.CONCLUDED
                });

                this.getNotifications();
            }
        });
    }

    public getNotifications(): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.get$.next();
    }

    public markAsRead(id: string): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.markAsRead$.next(id);
    }
}
